import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spinner } from '@fluentui/react-components';
import moment from 'moment';

import * as ComponentFunctions from './SettlementProtocolBreakdown.Functions';
import { GetSettlementProtocolBreakdown } from '../../../services/Settlements.Services';
import { SettlementProtocolBreakdown } from '../../../models/Entities/Settlements/SettlementProtocolBreakdown';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import {
    formatShare,
    hasBreakdownData,
    netByCurrency,
    protocolLabel,
    shouldShowBreakdown,
    sortDebts,
    sortProtocolAmounts,
} from './SettlementProtocolBreakdown.Logic';

import './SettlementProtocolBreakdown.Styles.css';

/**
 * Cuánto de la liquidación es de cada protocolo y cómo se reparten sus deudas.
 * Una liquidación a médico junta lo que hizo en varios protocolos; sin esto el
 * administrativo no sabe a qué protocolo cargar cada parte del pago.
 */
const SettlementProtocolBreakdownComponent = (props: ComponentFunctions.Props) => {
    const [breakdown, setBreakdown] = useState<SettlementProtocolBreakdown | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const visible = shouldShowBreakdown(props.Settlement);

    // Los títulos de protocolo viven en el store y la página de liquidación no
    // los carga por sí sola; sin ellos, acá y en las tablas de detalle sólo
    // habría números de protocolo.
    useEffect(() => {
        if (!props.hasProtocols) props.GetAllMedicalProtocols();
    }, [props.hasProtocols]);

    useEffect(() => {
        if (!visible || !props.accessToken || !props.SettlementId || props.Settlement?.isRecalculating) return;
        let cancelled = false;
        setIsLoading(true);
        setError(undefined);
        GetSettlementProtocolBreakdown(props.SettlementId, props.accessToken)
            .then((result) => { if (!cancelled) setBreakdown(result); })
            .catch((failure: any) => {
                if (cancelled) return;
                setError(failure?.ErrorMessage || failure?.message || 'No se pudo leer el desglose por protocolo.');
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
        // refreshKey resume estado, recálculo, deudas y totales: cualquiera de ellos cambia el reparto.
    }, [visible, props.SettlementId, props.accessToken, props.refreshKey]);

    if (!visible) return null;

    const fmt = (amount: number, currency?: string | null) => formatCurrencyAmountWithIssue(amount, currency || props.Settlement?.currencyCode);
    const amounts = sortProtocolAmounts(breakdown?.protocolAmounts, props.ProtocolTitleOf);
    const labelOf = (item: { protocolId?: number | null; protocolName?: string | null }) => protocolLabel(item, props.ProtocolTitleOf);
    const debts = sortDebts(breakdown?.debts);
    const totals = netByCurrency(breakdown?.protocolAmounts);
    const multiCurrency = totals.length > 1;

    return (
        <section className="spb-root" aria-label="Desglose por protocolo">
            <header className="spb-header">
                <span className="spb-title">Por protocolo</span>
                {isLoading && <Spinner size="extra-tiny" />}
            </header>

            {error && <p className="spb-error">{error}</p>}

            {!isLoading && !error && !hasBreakdownData(breakdown) && (
                <p className="spb-empty">Todavía no hay importes repartidos por protocolo.</p>
            )}

            {amounts.length > 0 && (
                <ul className="spb-list">
                    {amounts.map((item) => (
                        <li className="spb-item" key={`${item.protocolId ?? 'none'}-${item.currencyCode ?? ''}`}>
                            <div className="spb-item__row">
                                <span className="spb-item__name" title={labelOf(item)}>{labelOf(item)}</span>
                                <span className="spb-item__share">{formatShare(item.percentage)}</span>
                            </div>
                            <div className="spb-bar" aria-hidden="true">
                                <span className="spb-bar__fill" style={{ width: `${Math.min(100, Math.max(0, item.percentage || 0))}%` }} />
                            </div>
                            <div className="spb-item__amounts">
                                <span className="spb-item__net">{fmt(item.netAmount, item.currencyCode)}</span>
                                {item.withholding !== 0 && (
                                    <span className="spb-item__meta">
                                        honorario {fmt(item.ownerAmount, item.currencyCode)} · retención {fmt(item.withholding, item.currencyCode)}
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {totals.length > 0 && (
                <div className="spb-totals">
                    {totals.map((total) => (
                        <div className="spb-total" key={total.currency || 'none'}>
                            <span className="spb-total__label">Neto{multiCurrency ? ` ${total.currency}` : ''}</span>
                            <span className="spb-total__value">{fmt(total.net, total.currency)}</span>
                        </div>
                    ))}
                </div>
            )}

            {debts.length > 0 && (
                <div className="spb-debts">
                    <div className="spb-debts__title">
                        Deudas
                        {breakdown?.isDebtAllocationApproximate && (
                            <span
                                className="spb-approx"
                                title="Las deudas se reparten según el peso de cada protocolo; no es una imputación exacta."
                            >
                                reparto aproximado
                            </span>
                        )}
                    </div>
                    {debts.map((debt) => (
                        <div className="spb-debt" key={debt.debtId}>
                            <div className="spb-debt__row">
                                <span className="spb-debt__name">
                                    Deuda #{debt.debtId}
                                    <span className="spb-debt__date"> · {moment(debt.debtDate).format('DD/MM/YYYY')}</span>
                                </span>
                                <span className={`spb-debt__pending${debt.pendingAmount > 0 ? '' : ' spb-debt__pending--paid'}`}>
                                    {debt.pendingAmount > 0 ? `pendiente ${fmt(debt.pendingAmount, debt.currencyCode)}` : 'saldada'}
                                </span>
                            </div>
                            <div className="spb-debt__meta">
                                {fmt(debt.amount, debt.currencyCode)}
                                {debt.paidAmount > 0 && ` · pagado ${fmt(debt.paidAmount, debt.currencyCode)}`}
                                {debt.originalCurrencyCode && debt.originalCurrencyCode.toUpperCase() !== (debt.currencyCode || '').toUpperCase() && (
                                    ` · desde ${debt.originalCurrencyCode.toUpperCase()} a ${debt.exchangeRate}`
                                )}
                            </div>
                            {(debt.protocols?.length ?? 0) > 0 && (
                                <ul className="spb-debt__shares">
                                    {(debt.protocols ?? []).map((share) => (
                                        <li key={`${debt.debtId}-${share.protocolId ?? 'none'}`}>
                                            <span className="spb-debt__share-name">{labelOf(share)}</span>
                                            <span className="spb-debt__share-amount">
                                                {fmt(share.amount, debt.currencyCode)}
                                                {share.pendingAmount > 0 && <em> · pend. {fmt(share.pendingAmount, debt.currencyCode)}</em>}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementProtocolBreakdownComponent as any);
